import {
  checkLoginFailed,
  checkLoginSuccess,
  CHECK_LOGIN,
  clearState,
  IAuthActions,
  LOGIN,
  LOGIN_WITH_REFRESH_TOKEN,
  loginError,
  loginSuccess,
  LOGOUT,
  setUser,
} from "Actions";
import { Middleware } from "redux";
import { AuthToken } from "config";
import { userService, reinitializeApiServices } from "Apis/api";
import { middleEarthService } from "Apis/middleEarthApi";
import { saveAuthToken } from "Helpers/saveAuthToken";
import { IStoreState } from "Interfaces";
import { persistor } from "store";
import { getDomainEnvironment } from "features/auth/utils";
import { ApiAuthUser } from "@incendium/api";
import { AuthResponse, AuthTokens } from "Types/auth";

const maybeTrackContact = (u: ApiAuthUser) => {
  if (
    window.location.hostname.includes("usw.") ||
    window.location.hostname.includes("woo.")
  ) {
    (window as any).inc(
      "add_contact_data",
      {
        email: u.email,
        first_name: u.name,
        id: u.id,
      },
      true,
    );
  }
};

const establishSession = async (
  dispatch: (action: any) => unknown,
  result: AuthResponse,
): Promise<AuthTokens> => {
  let selectedEnv = getDomainEnvironment(result.envs || []);
  let tokens: AuthTokens = { ...result, selectedEnv };
  saveAuthToken(tokens);
  dispatch(loginSuccess(tokens));
  reinitializeApiServices();

  try {
    const u = await userService.userServiceGetAuthUser();
    if (!selectedEnv && u?.permissions?.[0]?.environment) {
      selectedEnv = u.permissions[0].environment;
      tokens = { ...tokens, selectedEnv };
      saveAuthToken(tokens);
      reinitializeApiServices();
      dispatch(loginSuccess(tokens));
    }
    if (u) {
      maybeTrackContact(u);
      dispatch(setUser(u));
    }
  } catch (userError) {
    console.warn("Could not fetch user data:", userError);
  }

  return tokens;
};

export const authMiddleware: Middleware<{}, IStoreState> =
  (store) => (next) => async (action: IAuthActions) => {
    const dispatch = store.dispatch;
    const state = store.getState();
    next(action);

    switch (action.type) {
      case LOGIN: {
        const { email, password, onSuccess, onFail } = action.payload;
        try {
          const result = await middleEarthService.login(email, password);
          const tokens = await establishSession(dispatch, result);
          onSuccess && onSuccess(tokens);
        } catch (e) {
          console.error(e);
          dispatch(loginError());
          onFail && onFail(e);
        }
        break;
      }
      case CHECK_LOGIN: {
        const token = state.auth.tokens;
        if (token && token.refresh_token) {
          try {
            const res = await middleEarthService.refreshToken(
              token.refresh_token,
            );
            const tokens = { ...res, selectedEnv: token.selectedEnv };
            saveAuthToken(tokens);
            reinitializeApiServices();
            await dispatch(checkLoginSuccess(tokens));
          } catch (e: any) {
            console.error("Token refresh failed:", e);
            dispatch(checkLoginFailed());
          }
        } else {
          dispatch(checkLoginFailed());
        }
        break;
      }
      case LOGIN_WITH_REFRESH_TOKEN: {
        const { refreshToken } = action.payload;
        try {
          const res = await middleEarthService.refreshToken(refreshToken);
          await establishSession(dispatch, res);
        } catch (e) {
          console.error("SAML refresh token exchange failed:", e);
          dispatch(loginError());
        }
        break;
      }
      case LOGOUT: {
        localStorage.removeItem(AuthToken);
        dispatch(clearState());
        setTimeout(() => {
          persistor.purge();
        }, 100);
        break;
      }
    }
  };
